<template>
  <div class="ma-4 px-4">
    <searchBar
      @add="openAddDialog"
      @updateSearch="(v) => (search = v)"
      headerText="Add-on Programme Referral"
      :add-field="false"
      @fileUploaded="initialise"
    />

    <!-- Partner Filter Radio -->
    <v-row style="margin-bottom: 3px">
      <v-col cols="12">
        <v-radio-group
          v-model="partnerFilter"
          row
          dense
          hide-details
          class="mt-0 pt-0"
        >
          <v-radio label="All" value="all" />
          <v-radio label="Assigned" value="assigned" />
          <v-radio label="Not Assigned" value="unassigned" />
        </v-radio-group>
      </v-col>
    </v-row>

    <v-row no-gutters>
      <v-col cols="12">
        <v-sheet outlined>
          <v-data-table
            :headers="headers"
            :search="search"
            :items="filteredAddons"
            :custom-filter="customFilter"
            fixed-header
            class="secondary row-height-50"
            :loading="loading"
          >
            <template #[`item.action`]="{ item }">
              <v-icon small class="mr-2" color="accent" @click="edit(item)">
                mdi-pencil
              </v-icon>
            </template>

            <template #[`item.usn_id`]="{ item }">
              {{ item.usn_id ? item.usn_id : "-" }}
            </template>

            <template #[`item.program_addons`]="{ item }">
              <div
                v-if="item.program_addons && item.program_addons.length"
                class="d-flex align-center flex-wrap"
              >
                <v-chip small class="mr-1 mb-1" color="primary" outlined>
                  {{ getAddonName(item.program_addons[0]) }}
                </v-chip>
                <v-chip
                  v-if="item.program_addons.length > 1"
                  small
                  class="mr-1 mb-1"
                  color="grey darken-1"
                  dark
                  style="cursor: pointer"
                  @click="showAllAddons(item)"
                >
                  +{{ item.program_addons.length - 1 }} other{{
                    item.program_addons.length - 1 > 1 ? "s" : ""
                  }}
                </v-chip>
              </div>
              <div v-else class="grey--text">No addons</div>
            </template>

            <template #[`item.status`]="{ item }">
              <v-chip small :color="getStatusColor(item.status)" dark>
                {{ item.status }}
              </v-chip>
            </template>

            <template #[`item.is_amount_paid`]="{ item }">
              <v-chip
                small
                :color="item.is_amount_paid ? 'green' : 'orange'"
                dark
              >
                {{ item.is_amount_paid ? "Paid" : "Pending" }}
              </v-chip>
            </template>

            <template #[`item.convinced_by_partner`]="{ item }">
              {{ getPartnerName(item.convinced_by_partner) }}
            </template>
          </v-data-table>
        </v-sheet>
      </v-col>
    </v-row>

    <!-- Edit Dialog -->
    <v-dialog v-model="dialog" width="600" persistent>
      <v-card>
        <v-card-title class="primary white--text">
          {{ editMode ? "Edit" : "View" }} Add-on Programme <Referral></Referral>
          <v-spacer></v-spacer>
          <v-icon
            @click="closeDialog"
            class="white--text"
            style="cursor: pointer"
            >mdi-close</v-icon
          >
        </v-card-title>

        <v-card-text class="mt-2">
          <v-container>
            <v-row>
              <v-col cols="6">
                <v-text-field
                  v-model="selectedItem.application_id"
                  label="Application ID"
                  dense
                  outlined
                  hide-details="auto"
                  readonly
                ></v-text-field>
              </v-col>
              <v-col cols="6">
                <v-text-field
                  v-model="selectedItem.usn_id"
                  label="USN ID"
                  dense
                  outlined
                  hide-details="auto"
                  readonly
                ></v-text-field>
              </v-col>
              <v-col cols="6">
                <v-text-field
                  v-model="selectedItem.amount"
                  label="Total Amount"
                  dense
                  outlined
                  hide-details="auto"
                  readonly
                  prefix="₹"
                ></v-text-field>
              </v-col>
              <v-col cols="6">
                <v-text-field
                  v-model="selectedItem.status"
                  label="Status"
                  dense
                  outlined
                  hide-details="auto"
                  readonly
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <div class="subtitle-2 font-weight-bold mb-2">
                   Add-on Programmes
                </div>
                <div
                  v-if="
                    selectedItem.program_addons &&
                    selectedItem.program_addons.length
                  "
                >
                  <v-chip
                    v-for="addonId in selectedItem.program_addons"
                    :key="addonId"
                    class="mr-2 mb-2"
                    color="primary"
                    outlined
                  >
                    {{ getAddonName(addonId) }}
                  </v-chip>
                </div>
                <div v-else class="grey--text">No addons selected</div>
              </v-col>
              <v-col cols="12">
                <v-select
                  v-model="selectedItem.convinced_by_partner"
                  :items="partners"
                  item-text="name"
                  item-value="id"
                  label="Add-on referral Partner*"
                  hide-details="auto"
                  :disabled="!editMode"
                  outlined
                  dense
                  clearable
                ></v-select>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" text @click="closeDialog">Cancel</v-btn>
          <v-btn
            v-if="editMode"
            color="primary"
            @click="updateConvincedByPartner"
            :loading="updating"
          >
            Update
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- All Addons Preview Dialog -->
    <v-dialog v-model="addonsPreviewDialog" max-width="500">
      <v-card>
        <v-card-title class="primary white--text">
          Programme Add-ons
          <v-spacer></v-spacer>
          <v-btn icon dark @click="addonsPreviewDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pa-4">
          <v-list dense class="py-0">
            <v-list-item
              v-for="(addonId, index) in previewAddonIds"
              :key="index"
              dense
              class="px-0"
            >
              <v-list-item-icon class="mr-2 my-1 pt-2">
                <v-icon small color="primary">mdi-package-variant</v-icon>
              </v-list-item-icon>
              <v-list-item-content class="py-1">
                <v-list-item-title class="text-body-2">
                  {{ getAddonName(addonId) }}
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>
          <div
            v-if="!previewAddonIds.length"
            class="text-center grey--text py-4"
          >
            No addons found
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import searchBar from "@/components/searchBarFilter.vue";
import { partnerProgramAddonsHeaders } from "@/constants/headers";

export default {
  components: { searchBar },
  data() {
    return {
      dialog: false,
      editMode: false,
      studentProgramAddons: [],
      selectedItem: {},
      partners: [],
      programAddonsMap: {},
      search: "",
      loading: false,
      updating: false,
      headers: partnerProgramAddonsHeaders,
      partnerFilter: "all",
      addonsPreviewDialog: false,
      previewAddonIds: [],
    };
  },
  computed: {
    filteredAddons() {
      if (this.partnerFilter === "assigned") {
        return this.studentProgramAddons.filter(
          (a) => !!a.convinced_by_partner,
        );
      } else if (this.partnerFilter === "unassigned") {
        return this.studentProgramAddons.filter((a) => !a.convinced_by_partner);
      }
      return this.studentProgramAddons;
    },
  },
  created() {
    this.$store.commit("setBreadcrumbs", [
      { text: "Add-on Programme Referral" },
    ]);
    this.initialise();
  },
  methods: {
    async initialise() {
      await this.getStudentProgramAddons();
      await this.fetchAllAddonNames();
      await this.getPartners();
    },

    async getStudentProgramAddons() {
      this.loading = true;
      try {
        const response = await this.$axios.get("/student_program_addons_with_details");
        this.studentProgramAddons = response.data.data.sort(
          (a, b) => b.id - a.id,
        );
      } catch (error) {
        this.$store.commit("showSnackbar", {
          message: error.response?.data?.message || "Failed to load data",
          color: "error",
        });
      } finally {
        this.loading = false;
      }
    },

    async fetchAllAddonNames() {
      const allAddonIds = new Set();
      this.studentProgramAddons.forEach((item) => {
        if (item.program_addons && Array.isArray(item.program_addons)) {
          item.program_addons.forEach((id) => allAddonIds.add(id));
        }
      });
      
      if (allAddonIds.size === 0) return;

      const addonIdsArray = Array.from(allAddonIds);
      try {
        const response = await this.$axios.get("/get_program_addon_names", {
          params: { ids: addonIdsArray.join(",") },
        });
        
        const responseData = response.data;
        let addons = [];
        
        if (responseData.program_addons) addons = responseData.program_addons;
        else if (responseData.data?.program_addons) addons = responseData.data.program_addons;
        else if (responseData.data?.names && Array.isArray(responseData.data.names)) {
          addons = addonIdsArray.map((id, index) => ({
            id,
            name: responseData.data.names[index] || `...`,
          }));
        } else if (Array.isArray(responseData)) addons = responseData;

        addons.forEach((addon) => {
          this.programAddonsMap[addon.id] = addon.name;
        });
      } catch (error) {
        console.error("Error fetching addon names:", error);
        addonIdsArray.forEach((id) => {
          this.programAddonsMap[id] = `Addon ${id}`;
        });
      }
    },

    getAddonName(addonId) {
      return this.programAddonsMap[addonId] || `...`;
    },

    async getPartners() {
      try {
        const response = await this.$axios.get("/partners");
        this.partners = response.data.data;
      } catch (error) {
        this.$store.commit("showSnackbar", {
          message: "Failed to load partners",
          color: "error",
        });
      }
    },

    getPartnerName(id) {
      if (!id) return "Not assigned";
      const partner = this.partners.find((p) => p.id === id);
      return partner ? partner.name : "Unknown Partner";
    },

    getStatusColor(status) {
      const colors = {
        active: "green",
        pending: "orange",
        completed: "blue",
        cancelled: "red",
      };
      return colors[status?.toLowerCase()] || "grey";
    },

    edit(item) {
      this.selectedItem = JSON.parse(JSON.stringify(item));
      this.editMode = true;
      this.dialog = true;
    },

    showAllAddons(item) {
      this.previewAddonIds = item.program_addons || [];
      this.addonsPreviewDialog = true;
    },

    openAddDialog() {
      this.$store.commit("showSnackbar", {
        message: "Add new record is not available. You can only edit existing records.",
        color: "info",
      });
    },

    async updateConvincedByPartner() {
      this.updating = true;
      try {
        const payload = {
          ...this.selectedItem,
          convinced_by_partner: this.selectedItem.convinced_by_partner || null,
        };
        
        await this.$axios.put(
          `/student_program_addons/${this.selectedItem.id}`,
          { student_program_addons: payload }
        );

        const index = this.studentProgramAddons.findIndex(
          (item) => item.id === this.selectedItem.id,
        );
        
        if (index !== -1) {
          this.studentProgramAddons[index].convinced_by_partner =
            this.selectedItem.convinced_by_partner;
        }

        this.$store.commit("showSnackbar", {
          message: "Partner updated successfully",
          color: "success",
        });
        this.closeDialog();
      } catch (error) {
        this.$store.commit("showSnackbar", {
          message: error.response?.data?.message || "Failed to update partner",
          color: "error",
        });
      } finally {
        this.updating = false;
      }
    },

    closeDialog() {
      this.dialog = false;
      this.editMode = false;
      this.selectedItem = {};
    },

    customFilter(value, search, item) {
      if (!search) return true;
      
      const partnerName = this.getPartnerName(item.convinced_by_partner);
      const addonNames = (item.program_addons || [])
        .map((id) => this.getAddonName(id))
        .join(" ");
      
      return (
        item.application_id?.toLowerCase().includes(search.toLowerCase()) ||
        item.usn_id?.toLowerCase().includes(search.toLowerCase()) ||
        item.status?.toLowerCase().includes(search.toLowerCase()) ||
        item.amount?.toString().includes(search) ||
        partnerName.toLowerCase().includes(search.toLowerCase()) ||
        addonNames.toLowerCase().includes(search.toLowerCase())
      );
    },
  },
};
</script>

<style scoped>
.row-height-50 >>> tbody tr {
  height: auto;
  min-height: 50px;
}
</style>